import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { usePublicAppSettings, useSlotBlocks } from "@/lib/queries";
import { nextWorkingDay, dayUnavailableReason, DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { fmtDate, dateIST } from "@emr/shared/india";

interface ReviewDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Current review state from the page (seeds the dialog when it opens).
  reviewNeeded: boolean;
  reviewDate: string; // YYYY-MM-DD
  // Called when the user clicks "Save & Print". The parent persists the
  // review (PATCH /visits/:id/review), reflects it on the page, and prints.
  onConfirm: (reviewNeeded: boolean, reviewDate: string) => void;
  // True while the parent is persisting + printing (disables buttons).
  busy?: boolean;
}

/**
 * Dialog shown when the user clicks "Print Prescription" but the visit has
 * no review date selected (reviewNeeded=false && reviewDate empty). Lets the
 * doctor pick a review date using the same chips + date picker as the ReviewBar
 * ("as per the original plan"), then Save & Print or Cancel.
 */
export function ReviewDateDialog({
  open, onOpenChange, reviewNeeded, reviewDate, onConfirm, busy,
}: ReviewDateDialogProps) {
  const { data: settings } = usePublicAppSettings();
  const { data: blocksData } = useSlotBlocks();

  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const slotBlocks = (blocksData?.rows ?? []).map((b) => ({
    date: typeof b.date === "string" ? b.date : dateIST(b.date as unknown as string),
    slot: b.slot,
    maxOverride: b.maxOverride,
    startOverride: b.startOverride,
    reason: b.reason,
  }));

  // Local draft state, seeded from the parent whenever the dialog opens.
  const [draftNeeded, setDraftNeeded] = useState(reviewNeeded);
  const [draftDate, setDraftDate] = useState(reviewDate);

  useEffect(() => {
    if (open) {
      setDraftNeeded(reviewNeeded);
      setDraftDate(reviewDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-default the draft date to +15 working days if empty and not PRN,
  // mirroring ReviewBar's auto-default behavior.
  useEffect(() => {
    if (!open) return;
    if (draftNeeded) return;
    if (draftDate) return;
    if (!settings || !blocksData) return;
    const d = nextWorkingDay(new Date(), 15, weeklySchedule, slotBlocks);
    if (d) setDraftDate(dateIST(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftNeeded, draftDate, settings, blocksData]);

  const chips = [5, 10, 15, 30, 60, 90, 120, 180];
  const applyChip = (n: number) => {
    const d = nextWorkingDay(new Date(), n, weeklySchedule, slotBlocks);
    if (d) setDraftDate(dateIST(d));
  };

  // Why the manually-picked date is unusable (null = fine). Save & print is
  // disabled while a scheduled review date lands on a blocked/off day; the
  // server (PATCH /visits/:id/review) remains the authoritative guard (422).
  const unavailable = !draftNeeded && draftDate
    ? dayUnavailableReason(new Date(draftDate + "T00:00:00"), weeklySchedule, slotBlocks)
    : null;

  const canConfirm = (draftNeeded || !!draftDate) && !unavailable;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(draftNeeded, draftDate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose review date</DialogTitle>
          <DialogDescription>
            Pick a review date before printing the prescription.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="review-needed-dialog"
              checked={draftNeeded}
              onCheckedChange={(v) => setDraftNeeded(v === true)}
            />
            <Label htmlFor="review-needed-dialog" className="text-sm">
              Review if needed
            </Label>
          </div>

          <div
            className={`flex flex-wrap items-center gap-2 transition-opacity ${
              draftNeeded ? "pointer-events-none opacity-40" : ""
            }`}
            aria-disabled={draftNeeded}
          >
            <Input
              type="date"
              value={draftDate}
              onChange={(e) => setDraftDate(e.target.value)}
              className="h-8 w-40"
              disabled={draftNeeded}
            />
            {unavailable && (
              <span className="text-xs font-medium text-red-600">
                {unavailable.kind === "blocked"
                  ? `Blocked${unavailable.reason ? ` — ${unavailable.reason}` : ""}`
                  : "Not a working day"}
              </span>
            )}
            <div className="flex flex-wrap gap-1">
              {chips.map((n) => {
                const d = nextWorkingDay(new Date(), n, weeklySchedule, slotBlocks);
                const label = d ? `+${n}d → ${fmtDate(d)}` : `+${n}d`;
                return (
                  <Button
                    key={n}
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => applyChip(n)}
                    disabled={draftNeeded}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>

          {draftNeeded && (
            <span className="text-xs text-muted">
              Review only if the patient needs it — no review appointment scheduled.
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || busy}
            className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
          >
            <Printer size={14} className="mr-1" />
            {busy ? "Saving…" : "Save & print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
